"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./Dialog";
import { Button } from "./styled-button";

type ButtonTypeColor =
  | "primary"
  | "secondary"
  | "danger"
  | "warning"
  | "success";

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  cancelText?: string;
  okText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: "default" | "danger";
  /** Overrides cancel button color (e.g. success for a safe secondary action). */
  cancelTypeColor?: ButtonTypeColor;
  /** Overrides confirm button color; falls back to `variant`. */
  okTypeColor?: ButtonTypeColor;
  cancelVariant?: "outline" | "filled" | "ghost" | "text";
  isLoading?: boolean;
  /** Prefix for data-testid on cancel/confirm buttons, e.g. `academies.bookings.cancel-dialog`. */
  testIdPrefix?: string;
}

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  cancelText,
  okText = "OK",
  onConfirm,
  onCancel,
  variant = "default",
  cancelTypeColor,
  okTypeColor,
  cancelVariant = "outline",
  isLoading = false,
  testIdPrefix,
}: ConfirmationDialogProps) {
  const confirmTypeColor =
    okTypeColor ?? (variant === "danger" ? "danger" : "primary");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px]">
        <DialogHeader className="flex flex-col items-center">
          <div className="flex gap-3 justify-start items-center w-full">
            {icon && <div>{icon}</div>}
            <DialogTitle className="text-start text-xl font-bold">
              {title}
            </DialogTitle>
          </div>
          <div className="flex flex-col gap-2 w-full px-8">
            {description && (
              <DialogDescription asChild>
                <div className="text-sm text-muted-foreground text-center sm:text-left mt-4">
                  {description}
                </div>
              </DialogDescription>
            )}
          </div>
        </DialogHeader>
        <DialogFooter>
          {cancelText && (
            <Button
              variant={cancelVariant}
              typeColor={cancelTypeColor}
              onClick={() => {
                onOpenChange(false);
                onCancel?.();
              }}
              disabled={isLoading}
              buttonLabel={cancelText}
              testId={
                testIdPrefix ? `${testIdPrefix}.cancel` : undefined
              }
            />
          )}
          <Button
            variant="filled"
            typeColor={confirmTypeColor}
            onClick={onConfirm}
            loading={isLoading}
            buttonLabel={okText}
            testId={testIdPrefix ? `${testIdPrefix}.confirm` : undefined}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
